/* ══════════════════════════════════════════════════════════════════════════
 * pdfToImage — render the first page of a PDF to a PNG object URL.
 *
 * Used by the CreateSchool configurator so an uploaded PDF drawing can be shown
 * as a decal on the 3D preview (three.js needs a raster image, not a PDF).
 *
 * pdf.js is dynamically imported so it stays out of the main bundle and only
 * loads the first time a customer actually uploads a PDF. The worker is pulled
 * from a CDN pinned to the installed pdfjs-dist version to avoid webpack worker
 * bundling headaches under CRA.
 * ══════════════════════════════════════════════════════════════════════════ */

// Cap the rendered raster so a large/high-DPI PDF can't blow up memory while
// still looking crisp as a chest/back print decal.
const MAX_DIMENSION = 2000;

let pdfjsPromise = null;

function loadPdfjs() {
  if (!pdfjsPromise) {
    pdfjsPromise = import('pdfjs-dist/legacy/build/pdf').then((pdfjs) => {
      pdfjs.GlobalWorkerOptions.workerSrc =
        `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
      return pdfjs;
    });
  }
  return pdfjsPromise;
}

/**
 * Un-matte a white background, in place.
 *
 * The page is treated as artwork composited over white. For each pixel we
 * derive how "white" it is from its brightest-removable component and turn
 * that into transparency (alpha = 255 − min channel), then un-multiply the
 * colour back out so the recovered artwork keeps its true colour. Because the
 * alpha is continuous, anti-aliased edges fade out smoothly with no white
 * halo — the leftover light-grey fringe the threshold approach left behind.
 *
 * pure white → fully transparent; pure black/colour → fully opaque, unchanged;
 * a 50%-grey edge → the artwork colour at 50% alpha.
 */
function unmatteWhite(ctx, width, height) {
  const image = ctx.getImageData(0, 0, width, height);
  const data = image.data;
  for (let i = 0; i < data.length; i += 4) {
    const origA = data[i + 3];
    if (origA === 0) continue; // already transparent — leave it

    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const min = Math.min(r, g, b);
    const a = 255 - min; // how far from white → new opacity

    if (a <= 0) {
      data[i + 3] = 0; // was white → fully transparent
      continue;
    }

    // Remove the white matte: recover the foreground colour that, over white,
    // produced this pixel. (observed = fg·α + white·(1−α), with white·(1−α)=min.)
    data[i] = ((r - min) * 255) / a;
    data[i + 1] = ((g - min) * 255) / a;
    data[i + 2] = ((b - min) * 255) / a;
    // Respect any pre-existing transparency in the PDF itself.
    data[i + 3] = origA < 255 ? Math.round((a * origA) / 255) : a;
  }
  ctx.putImageData(image, 0, 0);
}

/**
 * Convert a PDF File/Blob into a PNG object URL (first page only).
 * Returns the object URL string. Caller owns it and must revoke it.
 * Throws if the file can't be parsed/rendered.
 */
export async function pdfFileToPngUrl(file) {
  const pdfjs = await loadPdfjs();
  const buffer = await file.arrayBuffer();
  const doc = await pdfjs.getDocument({ data: buffer }).promise;
  try {
    const page = await doc.getPage(1);

    // Scale so the largest side hits (but doesn't exceed) MAX_DIMENSION.
    const baseViewport = page.getViewport({ scale: 1 });
    const scale = Math.min(
      MAX_DIMENSION / baseViewport.width,
      MAX_DIMENSION / baseViewport.height,
      // Never upscale a small PDF past 2x — keeps small vector logos sharp
      // without producing needlessly huge textures.
      2,
    );
    const viewport = page.getViewport({ scale: scale > 0 ? scale : 1 });

    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    const context = canvas.getContext('2d');

    // Render on a transparent canvas (don't fill white), then knock out the
    // PDF's white background so the artwork sits on the shirt with no white box.
    await page.render({ canvasContext: context, viewport }).promise;
    unmatteWhite(context, canvas.width, canvas.height);

    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('PDF render produced no image'))),
        'image/png',
      );
    });
    return URL.createObjectURL(blob);
  } finally {
    doc.cleanup();
    doc.destroy();
  }
}

/** True when the file looks like a PDF (by MIME type or extension). */
export function isPdfFile(file) {
  if (!file) return false;
  return file.type === 'application/pdf' || /\.pdf$/i.test(file.name || '');
}
